// Glossary.tsx - the tap-to-learn primitive (S19), extended in S22 to also host the Coach Drawer.
// <GlossaryProvider> renders the bottom-sheet popover + scrim once and exposes two openers via context:
//   open(key)            - a glossary term (the "Here:" example line).
//   openContent(content) - arbitrary content (the deep debrief, with its own bold example label).
// <Term term="ladder">evidence ladder</Term> is the inline dashed-underline trigger. Both openers fill
// the SAME popover DOM (mockup: the debrief drawer reused the glossary .pop/.scrim, injecting pt/pd/pe).
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { GLOSSARY } from '../data/glossary';

/** Normalized popover content - a glossary term and the coach debrief both project onto this. */
export interface PopoverContent {
  title: string;
  description: string;
  /** the bold label before the boxed example ("Here:" for terms, "Sizing:"/"Read:" for debriefs). */
  exampleLabel: string;
  example: string;
  /** optional scannable list rendered between the description and the boxed example. The archetype
   *  explainer (CX9) fills this with the type's concrete tells; terms + the coach debrief leave it unset
   *  and the list does not render, so this is a non-breaking addition to the shared popover. */
  bullets?: string[];
  /** optional label above the bullets list (CX9: "Tells"). Only rendered when `bullets` is set. */
  bulletsLabel?: string;
}

interface GlossaryContextValue {
  /** open the popover for a glossary term key. */
  open: (key: string) => void;
  /** open the popover with arbitrary content (the Coach Drawer path). */
  openContent: (content: PopoverContent) => void;
}

const GlossaryContext = createContext<GlossaryContextValue | null>(null);

export function useGlossary(): GlossaryContextValue {
  const ctx = useContext(GlossaryContext);
  if (!ctx) throw new Error('useGlossary must be used inside <GlossaryProvider>');
  return ctx;
}

export function GlossaryProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<PopoverContent | null>(null);

  const open = useCallback((key: string) => {
    const entry = GLOSSARY[key];
    if (entry) {
      setActive({
        title: entry.title,
        description: entry.description,
        exampleLabel: 'Here:',
        example: entry.example,
      });
    }
  }, []);
  const openContent = useCallback((content: PopoverContent) => setActive(content), []);
  const close = useCallback(() => setActive(null), []);

  return (
    <GlossaryContext.Provider value={{ open, openContent }}>
      {children}
      <div className={'scrim' + (active ? ' show' : '')} onClick={close} />
      <div className={'pop' + (active ? ' show' : '')} role="dialog" aria-modal="true" aria-hidden={!active}>
        <div className="grip" />
        <div className="pt">{active?.title}</div>
        <div className="pd">{active?.description}</div>
        {active?.bullets && active.bullets.length > 0 && (
          <div className="ps">
            {active.bulletsLabel && <div className="ps-lab">{active.bulletsLabel}</div>}
            <ul>
              {active.bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="pe">
          <b>{active?.exampleLabel}</b> {active?.example}
        </div>
        <button type="button" className="close" onClick={close}>
          Got it
        </button>
      </div>
    </GlossaryContext.Provider>
  );
}

export interface TermProps {
  term: string;
  children: ReactNode;
}

export function Term({ term, children }: TermProps) {
  const { open } = useGlossary();
  return (
    <button type="button" className="term" onClick={() => open(term)}>
      {children}
    </button>
  );
}
